import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Use useMemo to ensure GUEST_USER is only created once
    const GUEST_USER = useMemo(() => ({
        uid: "guest-user",
        username: "Guest",
        email: null,
        score: 0,
        token: null,
    }), []); // The empty dependency array means it's memoized once

    // Load user from localStorage on mount. If no user is found, set to guest.
    useEffect(() => {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
            try {
                const parsedUser = JSON.parse(storedUser);
                setUser(parsedUser);
            } catch (e) {
                console.error("Failed to parse user from localStorage", e);
                localStorage.removeItem("user"); // Clear corrupted data
                setUser(GUEST_USER); // Set to guest user on error
            }
        } else {
            // Set to guest user if no user is found in localStorage
            setUser(GUEST_USER);
        }
        setLoading(false); // Done checking for user
    }, [GUEST_USER]);

    const login = useCallback((token, uid, username, email, score) => {
        const userData = { token, uid, username, email, score };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
    }, []);

    const logout = useCallback(() => {
        setUser(GUEST_USER); // On logout, revert to the guest user
        localStorage.removeItem("user");
    }, [GUEST_USER]);

    // Derived auth flag: true when a real authenticated user exists (i.e., token is present)
    const isAuthenticated = !!(user && user.token);

    return (
        <AuthContext.Provider value={{ user, login, logout, loading, isAuthenticated }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);